<template>
  <div
    :class="[{ [$style.hasFailed]: hasFailed }]"
    data-test-id="ConfirmationCodeInput"
  >
    <div :class="$style.charInputGroup">
      <input
        ref="input1"
        v-model="codeNumbers.char1"
        :class="$style.charInput"
        maxlength="1"
        @keydown="handleInput"
        @keydown.arrow-left="handleArrowLeft"
        @keydown.arrow-right="handleArrowRight"
        @keydown.backspace="handleBackspace"
        @keypress.prevent
        @paste="handlePaste"
      ><input
        ref="input2"
        v-model="codeNumbers.char2"
        :class="$style.charInput"
        maxlength="1"
        @keydown="handleInput"
        @keydown.arrow-left="handleArrowLeft"
        @keydown.arrow-right="handleArrowRight"
        @keydown.backspace="handleBackspace"
        @keypress.prevent
        @paste="handlePaste"
      ><input
        ref="input3"
        v-model="codeNumbers.char3"
        :class="$style.charInput"
        maxlength="1"
        @keydown="handleInput"
        @keydown.arrow-left="handleArrowLeft"
        @keydown.arrow-right="handleArrowRight"
        @keydown.backspace="handleBackspace"
        @keypress.prevent
        @paste="handlePaste"
      >
    </div>
    <div :class="$style.charInputGroup">
      <input
        ref="input4"
        v-model="codeNumbers.char4"
        :class="$style.charInput"
        maxlength="1"
        @keydown="handleInput"
        @keydown.arrow-left="handleArrowLeft"
        @keydown.arrow-right="handleArrowRight"
        @keydown.backspace="handleBackspace"
        @keypress.prevent
        @paste="handlePaste"
      ><input
        ref="input5"
        v-model="codeNumbers.char5"
        :class="$style.charInput"
        maxlength="1"
        @keydown="handleInput"
        @keydown.arrow-left="handleArrowLeft"
        @keydown.arrow-right="handleArrowRight"
        @keydown.backspace="handleBackspace"
        @keypress.prevent
        @paste="handlePaste"
      ><input
        ref="input6"
        v-model="codeNumbers.char6"
        :class="$style.charInput"
        maxlength="1"
        @keydown="handleInput"
        @keydown.arrow-left="handleArrowLeft"
        @keydown.arrow-right="handleArrowRight"
        @keydown.backspace="handleBackspace"
        @keypress.prevent
        @paste="handlePaste"
      >
    </div>
  </div>
</template>

<script>
const codeInputRefs = ['input1', 'input2', 'input3', 'input4', 'input5', 'input6'];
const codeNumberKeys = ['char1', 'char2', 'char3', 'char4', 'char5', 'char6'];

export default {
  name: 'ConfirmationCodeInput',

  model: {
    prop: 'code',
    event: 'change',
  },

  props: {
    code: {
      type: String,
      required: true,
    },
    hasFailed: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
    resetFailedState: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    const codeNumbers = {};
    codeNumberKeys.forEach((key) => { codeNumbers[key] = ''; });
    return { codeNumbers };
  },

  computed: {
    codeValue() { return codeNumberKeys.map((key) => this.codeNumbers[key]).join(''); },
  },

  watch: {
    hasFailed(newValue) {
      // If the code is invalid, reset the code
      if (newValue === true) {
        codeNumberKeys.forEach((key) => { this.codeNumbers[key] = ''; });
        this.emitChange();
        this.$refs.input1.focus();
      }
    },
  },

  created() {
    // If a code is provided as a prop, populate input fields and update data
    if (this.code) this.populateNumberInputs(this.code);
  },

  mounted() {
    // Focus on the first input so users can start typing immediately
    if (!this.code) this.$refs.input1.focus();
  },

  methods: {
    emitChange() {
      // Emit a model change to update the `code` prop
      this.$emit('change', this.codeValue);
    },

    findEventInputIndex(event) {
      let inputIndex = -1;
      codeInputRefs.forEach((inputRef, index) => {
        if (event.currentTarget === this.$refs[inputRef]) {
          inputIndex = index;
        }
      });
      return inputIndex;
    },

    focusNextInput(inputIndex) {
      const nextInputRef = codeInputRefs[inputIndex + 1];
      if (nextInputRef) this.$refs[nextInputRef].focus();
    },

    focusPreviousInput(inputIndex) {
      const previousInputRef = codeInputRefs[inputIndex - 1];
      if (previousInputRef) this.$refs[previousInputRef].focus();
    },

    handleArrowLeft(event) {
      // Focus previous input on left arrow
      const inputIndex = this.findEventInputIndex(event);
      this.focusPreviousInput(inputIndex);
      event.preventDefault();
    },

    handleArrowRight(event) {
      // Focus next input on right arrow
      const inputIndex = this.findEventInputIndex(event);
      this.focusNextInput(inputIndex);
    },

    handleBackspace(event) {
      const inputIndex = this.findEventInputIndex(event);
      const currentInputValue = this.codeNumbers[codeNumberKeys[inputIndex]];
      if (currentInputValue) {
        // If in an input that already has a character, erase value
        this.codeNumbers[codeNumberKeys[inputIndex]] = '';
        this.emitChange();
      }
      if (!currentInputValue && inputIndex > 0) {
        // If in an empty input, erase previous input value and focus on that input field
        this.codeNumbers[codeNumberKeys[inputIndex - 1]] = '';
        this.focusPreviousInput(inputIndex);
        this.emitChange();
      }
      event.preventDefault();
    },

    handleInput(event) {
      this.resetFailedState();

      const { ctrlKey, key, metaKey } = event;
      const inputIndex = this.findEventInputIndex(event);

      // Update the input and focus next input on typing valid character or number
      if (key.length === 1 && /^[a-z0-9]+$/i.test(key) && !ctrlKey && !metaKey) {
        this.codeNumbers[codeNumberKeys[inputIndex]] = key.toUpperCase();
        this.emitChange();
        this.focusNextInput(inputIndex);

        // Submit code if the full 6-digit code has been entered
        if (this.codeValue.length === 6) this.onSubmit();
      }
    },

    handlePaste(event) {
      event.preventDefault();

      const startInputIndex = this.findEventInputIndex(event);
      const code = event.clipboardData.getData('text');
      this.populateNumberInputs(code, startInputIndex, true);

      // Submit code if the full 6-digit code has been entered
      if (this.codeValue.length === 6) this.onSubmit();
    },

    /**
     * Given a code, populate each input accordingly.
     * @param {String} code - The code to populate each input with
     * @param {Number} startInputIndex - The first input to start populating the code with
     * @param {Boolean} focusLastInput - Whether to focus on the last input after populating code
     */
    populateNumberInputs(code, startInputIndex = 0, focusLastInput = false) {
      // Filter out non-alphanumeric characters
      const codeArray = [...code].filter((char) => /^[a-z0-9]+$/i.test(char));

      // Assign each input the corresponding code character
      codeArray.forEach((codeNumber, index) => {
        const inputIndex = startInputIndex + index;
        if (inputIndex < codeNumberKeys.length) {
          this.codeNumbers[codeNumberKeys[inputIndex]] = codeNumber;
        }
      });
      this.emitChange();

      if (focusLastInput) {
        const lastInputIndex = Math.min(6, startInputIndex + codeArray.length) - 1;
        this.$refs[codeInputRefs[lastInputIndex]].focus();
      }
    },
  },
};
</script>

<style module>
@value (max-width-large) from '~css/breakpoints.css';

.charInputGroup {
  display: flex;
  flex: 0 1 auto;
  font-size: 1.5rem;
  margin: 0 1rem;
}

.charInput {
  background: var(--jcWhite);
  border: 0;
  font-size: 1em;
  height: 3.4375em;
  margin-bottom: 0.25rem;
  margin-right: 0.25rem;
  text-align: center;
  width: 3.4375em;
}

.charInput:focus {
  border-color: var(--jcPrimary);
  outline: none;
}

.hasFailed .charInput {
  border-color: var(--jcError);
}

@media (min-width: max-width-large) {
  .charInputGroup {
    font-size: 2rem;
  }
}
</style>
